import { ElementBuilder } from "@universityofmaryland/web-builder-library";
import * as token from "@universityofmaryland/web-token-library";
import { card } from "@universityofmaryland/web-elements-library/composite";
import { gridOffset, stacked } from "@universityofmaryland/web-elements-library/layout";
import { LoadingState } from "../../states/loading.js";
import { EmptyState } from "../../states/empty.js";
import { PaginationState } from "../../states/pagination.js";
import { Announcer } from "../../states/announcer.js";
import { expertsFetchStrategy } from "../../strategies/fetch/experts.js";
import { inTheNewsFetchStrategy } from "../../strategies/fetch/inTheNews.js";
import { expertsDisplayStrategy } from "../../strategies/display/experts.js";
import { inTheNewsDisplayStrategy } from "../../strategies/display/inTheNews.js";
import { dispatch, eventNames } from "../../helpers/events/index.js";
import { setShadowStyles } from "../../helpers/styles/shadow.js";
const DEFAULT_ROWS_TO_START = 5;
const MIN_ROWS_TO_START = 2;
const MAX_ROWS_TO_START = 10;
const LOAD_MORE_ITEMS = 5;
const normalizeRowCount = (rows) => {
  if (rows === void 0) return DEFAULT_ROWS_TO_START;
  return Math.max(MIN_ROWS_TO_START, Math.min(MAX_ROWS_TO_START, rows));
};
const createExpertFetchProps = (token2, expertId) => ({
  token: token2,
  limit: 1,
  offset: 0,
  ids: [expertId]
});
const createNewsFetchProps = (token2, expertId, numberOfRowsToStart, offset) => ({
  token: token2,
  expertId,
  numberOfRowsToStart,
  getOffset: () => offset
});
const createAnnouncerMessage = (showing, total, isLazyLoad) => isLazyLoad ? `Showing ${showing} of ${total} news articles` : `Showing ${showing} news articles`;
class InTheNewsFeedState {
  constructor(initialStyles) {
    this.stylesArray = [];
    this.shadowRoot = null;
    this.totalEntries = 0;
    this.offset = 0;
    this.pagination = null;
    this.stylesArray.push(initialStyles);
  }
  addStyles(styles) {
    this.stylesArray.push(styles);
  }
  setShadowRoot(shadow) {
    this.shadowRoot = shadow;
  }
  async updateShadowStyles() {
    if (!this.shadowRoot) return;
    await setShadowStyles({
      shadowRoot: this.shadowRoot,
      styles: this.getStyles()
    });
  }
  getStyles() {
    return this.stylesArray.join("\n");
  }
  getShadowCallback() {
    return (shadow) => this.setShadowRoot(shadow);
  }
  getOffset() {
    return this.offset;
  }
  setOffset(value) {
    this.offset = value;
  }
  incrementOffset(count) {
    this.offset += count;
  }
  getTotalEntries() {
    return this.totalEntries;
  }
  setTotalEntries(total) {
    this.totalEntries = total;
  }
  getPagination() {
    return this.pagination;
  }
  setPagination(pagination) {
    this.pagination = pagination;
  }
}
const createExpertOverlayCard = (expert, isThemeDark) => expertsDisplayStrategy.mapEntryToCard(expert, {
  isOverlay: true,
  isThemeDark
});
const createNewsListContainer = (entries, state, isThemeDark) => {
  const stackedLayout = stacked({ isThemeDark, showDividers: true, gap: "0" });
  stackedLayout.element.id = "umd-expert-in-the-news-list";
  state.addStyles(stackedLayout.styles);
  entries.forEach((entry) => {
    const newsCard = inTheNewsDisplayStrategy.mapEntryToCard(entry, {
      isThemeDark,
      cardType: "list"
    });
    stackedLayout.element.appendChild(newsCard.element);
    state.addStyles(newsCard.styles);
  });
  return stackedLayout.element;
};
const appendNewsEntries = (container, entries, state, isThemeDark) => {
  const listContainer = container.querySelector(
    "#umd-expert-in-the-news-list"
  );
  if (!listContainer) return;
  entries.forEach((entry) => {
    const newsCard = inTheNewsDisplayStrategy.mapEntryToCard(entry, {
      isThemeDark,
      cardType: "list"
    });
    listContainer.appendChild(newsCard.element);
    state.addStyles(newsCard.styles);
  });
};
const renderLayout = async (container, expert, newsEntries, state, props, loadMore) => {
  const { isThemeDark = false, isLazyLoad = false } = props;
  const offsetLayout = gridOffset({
    columns: 2,
    isLayoutReversed: false
  });
  const expertCard = createExpertOverlayCard(expert, isThemeDark);
  state.addStyles(expertCard.styles);
  state.addStyles(`
    .${card.overlay.imageClassRef} {
      height: inherit;
    }

    @media (${token.media.queries.tablet.min}) {
      .${card.overlay.imageClassRef} .card-overlay-image-container {
        min-height: 560px;
      }
    }

    .${card.overlay.imageClassRef} .umd-asset-image-wrapper-scaled {
      position: absolute;
    }
  `);
  const rightColumn = document.createElement("div");
  rightColumn.className = "expert-in-the-news-right-column";
  const newsList = createNewsListContainer(newsEntries, state, isThemeDark);
  rightColumn.appendChild(newsList);
  offsetLayout.element.appendChild(expertCard.element);
  offsetLayout.element.appendChild(rightColumn);
  container.appendChild(offsetLayout.element);
  state.addStyles(offsetLayout.styles);
  state.setOffset(newsEntries.length);
  if (isLazyLoad && state.getTotalEntries() > state.getOffset()) {
    const pagination = new PaginationState({
      totalEntries: state.getTotalEntries(),
      offset: state.getOffset(),
      isLazyLoad: true,
      callback: loadMore,
      isThemeDark
    });
    const paginationElement = pagination.render(rightColumn);
    if (paginationElement) state.addStyles(paginationElement.styles);
    state.setPagination(pagination);
  }
  const announcer = new Announcer({
    message: createAnnouncerMessage(
      state.getOffset(),
      state.getTotalEntries(),
      isLazyLoad
    )
  });
  container.appendChild(announcer.getElement());
  await state.updateShadowStyles();
};
const renderError = async (container, message, state, isThemeDark) => {
  const emptyState = new EmptyState({ message, isThemeDark });
  emptyState.render(container);
  state.addStyles(emptyState.styles);
  await state.updateShadowStyles();
};
const renderLayoutWithNoNews = async (container, expert, state, isThemeDark) => {
  const offsetLayout = gridOffset({
    columns: 2,
    isLayoutReversed: false
  });
  const expertCard = createExpertOverlayCard(expert, isThemeDark);
  state.addStyles(expertCard.styles);
  state.addStyles(`
    .${card.overlay.imageClassRef} {
      height: inherit;
    }

    @media (${token.media.queries.tablet.min}) {
      .${card.overlay.imageClassRef} .card-overlay-image-container {
        min-height: 560px;
      }
    }

    .${card.overlay.imageClassRef} .umd-asset-image-wrapper-scaled {
      position: absolute;
    }
  `);
  const rightColumn = document.createElement("div");
  rightColumn.className = "expert-in-the-news-right-column";
  const emptyState = new EmptyState({
    message: "No news articles found for this expert",
    isThemeDark
  });
  emptyState.render(rightColumn);
  state.addStyles(emptyState.styles);
  offsetLayout.element.appendChild(expertCard.element);
  offsetLayout.element.appendChild(rightColumn);
  container.appendChild(offsetLayout.element);
  state.addStyles(offsetLayout.styles);
  await state.updateShadowStyles();
};
const logError = (errorType, expertId, error) => {
  const messages = {
    expert_not_found: `No expert found with ID "${expertId}". Verify the expert ID is correct.`,
    no_news: `No news articles found for expert "${expertId}".`,
    graphql_error: `GraphQL error occurred for expert "${expertId}".`
  };
  console.warn(`[Expert In The News Feed] ${messages[errorType]}`, error || "");
};
const expertsInTheNews = (props) => {
  const { token: token2, expertId, isThemeDark = false, isLazyLoad = false } = props;
  const numberOfRowsToStart = normalizeRowCount(props.numberOfRowsToStart);
  const containerBuilder = new ElementBuilder("div").withClassName(
    "expert-in-the-news-feed"
  );
  const container = containerBuilder.getElement();
  const loading = new LoadingState({ isThemeDark });
  const state = new InTheNewsFeedState(loading.styles);
  const loadMore = async () => {
    const pagination = state.getPagination();
    if (pagination) pagination.remove();
    loading.show(container);
    const fetchProps = createNewsFetchProps(
      token2,
      expertId,
      LOAD_MORE_ITEMS,
      state.getOffset()
    );
    const variables = inTheNewsFetchStrategy.composeApiVariables(fetchProps);
    const entries = await inTheNewsFetchStrategy.fetchEntries(variables);
    loading.hide();
    if (!entries || entries.length === 0) return;
    appendNewsEntries(container, entries, state, isThemeDark);
    state.incrementOffset(entries.length);
    if (pagination) {
      pagination.updateState(state.getOffset(), state.getTotalEntries());
      if (pagination.styles) state.addStyles(pagination.styles);
      await state.updateShadowStyles();
    }
    const existingAnnouncer = container.querySelector(
      '[role="status"]'
    );
    if (existingAnnouncer) {
      existingAnnouncer.textContent = createAnnouncerMessage(
        state.getOffset(),
        state.getTotalEntries(),
        isLazyLoad
      );
    }
    dispatch(
      container,
      eventNames.FEED_LOADED_MORE,
      { items: entries, count: entries.length, total: state.getTotalEntries() }
    );
  };
  const initialize = async () => {
    if (!expertId) {
      await renderError(container, "Expert ID is required", state, isThemeDark);
      return;
    }
    loading.show(container);
    try {
      const expertFetchProps = createExpertFetchProps(token2, expertId);
      const expertVariables = expertsFetchStrategy.composeApiVariables(expertFetchProps);
      const newsFetchProps = createNewsFetchProps(
        token2,
        expertId,
        numberOfRowsToStart,
        0
      );
      const newsVariables = inTheNewsFetchStrategy.composeApiVariables(newsFetchProps);
      const [expertEntries, newsCount, newsEntries] = await Promise.all([
        expertsFetchStrategy.fetchEntries(expertVariables),
        inTheNewsFetchStrategy.fetchCount(newsVariables),
        inTheNewsFetchStrategy.fetchEntries(newsVariables)
      ]);
      loading.hide();
      if (!expertEntries || expertEntries.length === 0) {
        logError("expert_not_found", expertId);
        await renderError(container, "Expert not found", state, isThemeDark);
        return;
      }
      if (!newsEntries || newsEntries.length === 0) {
        logError("no_news", expertId);
        await renderLayoutWithNoNews(
          container,
          expertEntries[0],
          state,
          isThemeDark
        );
        return;
      }
      state.setTotalEntries(newsCount || newsEntries.length);
      dispatch(
        container,
        eventNames.FEED_LOADED,
        {
          expert: expertEntries[0],
          newsItems: newsEntries,
          newsCount: newsEntries.length,
          total: state.getTotalEntries()
        }
      );
      await renderLayout(
        container,
        expertEntries[0],
        newsEntries,
        state,
        props,
        loadMore
      );
    } catch (error) {
      loading.hide();
      logError("graphql_error", expertId, error);
      await renderError(
        container,
        "Failed to load content",
        state,
        isThemeDark
      );
    }
  };
  initialize();
  return {
    element: containerBuilder.build().element,
    get styles() {
      return state.getStyles();
    },
    events: {
      callback: state.getShadowCallback()
    }
  };
};
export {
  expertsInTheNews
};
//# sourceMappingURL=in-the-news.js.map
